/**
 * Pagewell working-document store.
 *
 * Holds the imported source docs and the ordered list of page instances the
 * user is arranging. All mutating operations are pure functions over the page
 * array (exported separately so they can be unit-tested without the DOM), and
 * the store wraps them with an undo/redo history and change notifications.
 */

import type { PageItem, PagePlanEntry, SourceDoc } from './types';

let pageSeq = 0;
/** Deterministic-enough unique id without Math.random (also worker/test safe). */
function nextPageId(): string {
  pageSeq += 1;
  return `p${pageSeq}`;
}

// ── pure operations over a page array ─────────────────────────────────────

export function normaliseRotation(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Move the page with `id` so it lands at `toIndex` in the array order. */
export function movePage(pages: PageItem[], id: string, toIndex: number): PageItem[] {
  const from = pages.findIndex((p) => p.id === id);
  if (from === -1) return pages;
  const clampedTo = Math.max(0, Math.min(toIndex, pages.length - 1));
  if (from === clampedTo) return pages;
  const next = pages.slice();
  const [moved] = next.splice(from, 1);
  next.splice(clampedTo, 0, moved);
  return next;
}

/** Rotate a single page by `delta` degrees (clockwise). */
export function rotatePage(pages: PageItem[], id: string, delta: number): PageItem[] {
  return pages.map((p) =>
    p.id === id ? { ...p, rotation: normaliseRotation(p.rotation + delta) } : p,
  );
}

/** Rotate every page (or every selected page, if any are selected) by `delta`. */
export function rotateAll(pages: PageItem[], delta: number): PageItem[] {
  const anySelected = pages.some((p) => p.selected);
  return pages.map((p) =>
    !anySelected || p.selected ? { ...p, rotation: normaliseRotation(p.rotation + delta) } : p,
  );
}

/** Remove the pages whose ids are in `ids`. */
export function deletePages(pages: PageItem[], ids: string[]): PageItem[] {
  const drop = new Set(ids);
  return pages.filter((p) => !drop.has(p.id));
}

export function toggleSelect(pages: PageItem[], id: string): PageItem[] {
  return pages.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p));
}

export function setAllSelected(pages: PageItem[], selected: boolean): PageItem[] {
  return pages.map((p) => (p.selected === selected ? p : { ...p, selected }));
}

/**
 * Build the assembly plan. If `onlySelected` and at least one page is selected,
 * only selected pages are included; otherwise all pages are, in visual order.
 */
export function buildPlan(pages: PageItem[], onlySelected: boolean): PagePlanEntry[] {
  const anySelected = pages.some((p) => p.selected);
  const use = onlySelected && anySelected ? pages.filter((p) => p.selected) : pages;
  return use.map((p) => ({ docId: p.docId, pageIndex: p.pageIndex, rotation: p.rotation }));
}

/** Create fresh page instances for a newly-imported source doc. */
export function pagesForDoc(doc: SourceDoc): PageItem[] {
  const out: PageItem[] = [];
  for (let i = 0; i < doc.pageCount; i++) {
    out.push({ id: nextPageId(), docId: doc.id, pageIndex: i, rotation: 0, selected: false });
  }
  return out;
}

// ── store with history ────────────────────────────────────────────────────

const MAX_HISTORY = 60;

export class OrganizerStore {
  private docs = new Map<string, SourceDoc>();
  pages: PageItem[] = [];
  private history: PageItem[][] = [];
  private future: PageItem[][] = [];
  private listeners = new Set<() => void>();

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  /** Snapshot current pages onto the undo stack before a mutation. */
  private checkpoint(): void {
    this.history.push(this.pages.map((p) => ({ ...p })));
    if (this.history.length > MAX_HISTORY) this.history.shift();
    this.future = [];
  }

  /** Apply a pure op with history + notification. */
  private apply(next: PageItem[]): void {
    if (next === this.pages) return; // no-op ops return the same ref
    this.checkpoint();
    this.pages = next;
    this.emit();
  }

  getDoc(id: string): SourceDoc | undefined {
    return this.docs.get(id);
  }

  allDocs(): SourceDoc[] {
    return [...this.docs.values()];
  }

  get docCount(): number {
    return this.docs.size;
  }

  addDoc(doc: SourceDoc): void {
    this.checkpoint();
    this.docs.set(doc.id, doc);
    this.pages = [...this.pages, ...pagesForDoc(doc)];
    this.emit();
  }

  move(id: string, toIndex: number): void {
    this.apply(movePage(this.pages, id, toIndex));
  }

  rotate(id: string, delta: number): void {
    this.apply(rotatePage(this.pages, id, delta));
  }

  rotateAll(delta: number): void {
    this.apply(rotateAll(this.pages, delta));
  }

  remove(ids: string[]): void {
    if (ids.length === 0) return;
    this.apply(deletePages(this.pages, ids));
  }

  toggleSelect(id: string): void {
    // selection changes are not history-worthy — mutate + emit without checkpoint
    this.pages = toggleSelect(this.pages, id);
    this.emit();
  }

  setAllSelected(selected: boolean): void {
    this.pages = setAllSelected(this.pages, selected);
    this.emit();
  }

  get selectedCount(): number {
    return this.pages.reduce((n, p) => n + (p.selected ? 1 : 0), 0);
  }

  plan(onlySelected: boolean): PagePlanEntry[] {
    return buildPlan(this.pages, onlySelected);
  }

  /** docId -> bytes, only for docs actually referenced by the given plan. */
  sourcesFor(plan: PagePlanEntry[]): Record<string, ArrayBuffer> {
    const out: Record<string, ArrayBuffer> = {};
    for (const entry of plan) {
      if (out[entry.docId]) continue;
      const doc = this.docs.get(entry.docId);
      if (doc) out[entry.docId] = doc.bytes;
    }
    return out;
  }

  canUndo(): boolean {
    return this.history.length > 0;
  }

  canRedo(): boolean {
    return this.future.length > 0;
  }

  undo(): void {
    const prev = this.history.pop();
    if (!prev) return;
    this.future.push(this.pages.map((p) => ({ ...p })));
    this.pages = prev;
    this.emit();
  }

  redo(): void {
    const next = this.future.pop();
    if (!next) return;
    this.history.push(this.pages.map((p) => ({ ...p })));
    this.pages = next;
    this.emit();
  }

  reset(): void {
    this.docs.clear();
    this.pages = [];
    this.history = [];
    this.future = [];
    this.emit();
  }

  get pageCount(): number {
    return this.pages.length;
  }
}
